import React from 'react';
import { useSmoothLoading } from 'lib/use-smooth-loading';
import * as XLSX from 'xlsx-js-style';
import { Download, ArrowUp, ArrowDown } from 'lucide-react';
import { Skeleton } from 'components/ui/skeleton'; 

export interface PnlSummaryTableRow {
  MIS_Description: string;
  PY: number;
  ABP: number;
  CY: number;
  Growth_Amt: number | null;
  Growth_Pct: number | null;
  Achievement_Amt: number | null;
  Achievement_Pct: number | null;
}

interface PnlSummaryTableProps {
  data: PnlSummaryTableRow[];
  loading: boolean;
  conversionType: 'INR' | 'Lakhs' | 'Crores';
  onConversionChange: (type: 'INR' | 'Lakhs' | 'Crores') => void;
  showDownloadButton?: boolean;
  hasLoadedOnce?: boolean;
}

const PnlSummaryTable: React.FC<PnlSummaryTableProps> = ({ 
  data, 
  loading, 
  conversionType, 
  onConversionChange,
  showDownloadButton,
  hasLoadedOnce
}) => {
  const { isLoading, showContent } = useSmoothLoading(loading, {
    minLoadingTime: 150,
    debounceTime: 150
  });

  const formatNumber = (value: number | null, isPercent = false): string => {
    if (value === null || value === undefined) return '-';
    if (value === 0) return isPercent ? '0.00%' : '0.00';

    let displayValue = value;
    if (!isPercent) {
      switch (conversionType) {
        case 'Lakhs':
          displayValue = value / 100000;
          break;
        case 'Crores':
          displayValue = value / 10000000;
          break;
        case 'INR':
        default:
          displayValue = value;
          break;
      }
    }
    
    const formatted = displayValue.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return isPercent ? `${formatted}%` : formatted;
  }; 

  const isPercentRow = (description: string): boolean => {
    return description.includes('%');
  };

  const isKeyMetricRow = (description: string): boolean => {
    return (
      description.startsWith('CM') ||
      description === 'EBIT' ||
      description === 'PBT' ||
      description === 'Total Revenues'
    );
  };

  const getGrowthColorClass = (value: number | null): string => {
    if (value === null || value === undefined || value === 0) return '';
    return value > 0 ? 'text-green-700' : 'text-red-600';
  };

  const handleDownloadExcel = () => {
    const excelData: any[] = [];
    
    const headers = [
      'Particulars', 'PY', 'ABP', 'CY', 'Growth Amount', 'Growth %', 'Achievement Amount', 'Achievement %'
    ];
    excelData.push(headers);

    data.forEach((row) => {
      const isPercent = isPercentRow(row.MIS_Description);
      excelData.push([
        row.MIS_Description,
        formatNumber(row.PY, isPercent),
        formatNumber(row.ABP, isPercent),
        formatNumber(row.CY, isPercent),
        formatNumber(row.Growth_Amt, isPercent),
        formatNumber(row.Growth_Pct, true),
        formatNumber(row.Achievement_Amt, isPercent),
        formatNumber(row.Achievement_Pct, true)
      ]);
    });

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(excelData);
    const totalRows = excelData.length;

    worksheet['!rows'] = Array.from({ length: totalRows }).map((_, index) => {
      if (index === 0) {
        return { hpt: 24 };
      }
      return { hpt: 16 };
    });

    const headerRange = XLSX.utils.decode_range(worksheet['!ref']!);

    for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      worksheet[cellAddress].s = {
        font: {
          bold: true,
          color: { rgb: "000000" }
        },
        fill: {
          patternType: "solid",
          fgColor: { rgb: "D9D9D9" }
        },
        alignment: {
          horizontal: "center",
          vertical: "center"
        },
        border: {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" }
        }
      };
    }

    const columnWidths = [
      { wch: 30 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 18 },
      { wch: 12 },
      { wch: 18 },
      { wch: 15 }
    ];
    worksheet['!cols'] = columnWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Performance Comparison Data');

    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
    const filename = `Performance_Comparison_Data_${conversionType}_${timestamp}.xlsx`;

    XLSX.writeFile(workbook, filename);
  };

  const opacityClass =
    isLoading || showContent ? 'opacity-100' : 'opacity-0';

  const renderLoadingRows = () => {
    const rows = 19;  
    const cols = 8;

    return Array.from({ length: rows }).map((_, rowIndex) => (
      <tr key={`skeleton-row-${rowIndex}`} className="hover:bg-gray-50">
        {Array.from({ length: cols }).map((_, colIndex) => (
          <td
            key={`skeleton-cell-${rowIndex}-${colIndex}`}
            className="border border-gray-200 px-2 py-1.5"
          >
            <Skeleton
              className={`h-4 ${colIndex === 0 ? 'w-3/4' : 'w-full'}`}
            />
          </td>
        ))}
      </tr>
    ));
  };

  return (
    <div className={`mt-6 space-y-2 transition-opacity duration-300 ${opacityClass}`}>
      {/* Controls Row */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2">
          <button 
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              conversionType === 'INR' 
                ? 'bg-blue-600 text-white' 
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
            onClick={() => onConversionChange('INR')}
          >
            INR
          </button>
          <button 
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              conversionType === 'Lakhs' 
                ? 'bg-blue-600 text-white' 
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
            onClick={() => onConversionChange('Lakhs')}
          >
            Lakhs
          </button>
          <button 
            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
              conversionType === 'Crores' 
                ? 'bg-blue-600 text-white' 
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
            onClick={() => onConversionChange('Crores')}
          >
            Crores
          </button>
        </div>
        <div className="flex gap-2">
          {showDownloadButton && (
            <button
              className="px-3 py-1 text-sm rounded-md border border-green-600 text-green-700 hover:bg-green-50 transition-colors flex items-center gap-1"
              onClick={handleDownloadExcel}
            >
              <Download className="w-4 h-4" />
              Excel
            </button>
          )}
        </div>
      </div>

      {/* Table Container */}
      <div className="w-full overflow-x-auto border border-gray-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            {/* First header row with merged cells */}
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold min-w-[200px]" rowSpan={2}>
                Particulars
              </th>
              <th className="border border-gray-300 border-l-4 px-2 py-2 text-center text-gray-700 font-bold" colSpan={3}>
                YTD
              </th>
              <th className="border border-gray-300 border-l-4 px-2 py-2 text-center text-gray-700 font-bold" colSpan={2}>
                Growth (CY vs PY)
              </th>
              <th className="border border-gray-300 border-l-4 px-2 py-2 text-center text-gray-700 font-bold" colSpan={2}>
                Achievement (CY vs ABP)
              </th>
            </tr>
            {/* Second header row */}
            <tr className="bg-gray-100">
              <th className="border border-gray-300 border-l-4 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                PY
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                ABP
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                CY
              </th>
              {/* Growth Amount */}
              <th className="border border-gray-300 border-l-4 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                Amount
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                %
              </th>
              {/* Achievement Amount */}
              <th className="border border-gray-300 border-l-4 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                Amount
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                %
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              renderLoadingRows()
            ) : (
              data.map((row, index) => {
                const isPercent = isPercentRow(row.MIS_Description);
                const isKeyMetric = isKeyMetricRow(row.MIS_Description);

                const growthAmtColor = getGrowthColorClass(row.Growth_Amt);
                const growthPctColor = getGrowthColorClass(row.Growth_Pct);
                const achievementAmtColor = getGrowthColorClass(row.Achievement_Amt);

                const achievementPctDiff =
                  row.Achievement_Pct !== null && row.Achievement_Pct !== undefined
                    ? row.Achievement_Pct - 100
                    : null;
                const achievementPctColor = getGrowthColorClass(achievementPctDiff);

                return (
                  <tr 
                    key={index}
                    className={`hover:bg-gray-50 ${
                      isPercent ? 'bg-gray-100' : ''
                    }`}
                  >
                    <td className="border border-gray-200 px-2 py-1.5 text-left font-bold">
                      {row.MIS_Description}
                    </td>

                    {/* PY */}
                    <td className={`border border-gray-200 border-l-4 px-2 py-1.5 text-right ${isKeyMetric ? 'font-semibold' : ''}`}>
                      {formatNumber(row.PY, isPercent)}
                    </td>

                    {/* ABP */}
                    <td className={`border border-gray-200 px-2 py-1.5 text-right ${isKeyMetric ? 'font-semibold' : ''}`}>
                      {formatNumber(row.ABP, isPercent)}
                    </td>

                    {/* CY */}
                    <td className={`border border-gray-200 px-2 py-1.5 text-right ${isKeyMetric ? 'font-semibold' : ''}`}>
                      {formatNumber(row.CY, isPercent)}
                    </td>

                    {/* Growth Amount */}
                    <td className="border border-gray-200 border-l-4 px-2 py-1.5 text-right ">
                      {isKeyMetric ? (
                        <div className="flex items-center justify-end gap-1">
                          {row.Growth_Amt !== null && row.Growth_Amt !== 0 && (
                            row.Growth_Amt > 0 ? (
                              <ArrowUp className={`w-3 h-3 ${growthAmtColor}`} />
                            ) : (
                              <ArrowDown className={`w-3 h-3 ${growthAmtColor}`} />
                            )
                          )}
                          <span className={`${isKeyMetric ? 'font-semibold' : ''} ${growthAmtColor}`}>
                            {formatNumber(row.Growth_Amt, isPercent)}
                          </span>
                        </div>
                      ) : (
                        formatNumber(row.Growth_Amt, isPercent)
                      )}
                    </td>

                    {/* Growth % */}
                    <td className="border border-gray-200 px-2 py-1.5 text-right">
                      {isKeyMetric ? (
                        <div className="flex items-center justify-end gap-1">
                          {row.Growth_Pct !== null && row.Growth_Pct !== 0 && (
                            row.Growth_Pct > 0 ? (
                              <ArrowUp className={`w-3 h-3 ${growthPctColor}`} />
                            ) : (
                              <ArrowDown className={`w-3 h-3 ${growthPctColor}`} />
                            )
                          )}
                          <span className={`${isKeyMetric ? 'font-semibold' : ''} ${growthPctColor}`}>
                            {formatNumber(row.Growth_Pct, true)}
                          </span>
                        </div>
                      ) : (
                        formatNumber(row.Growth_Pct, true)
                      )}
                    </td>

                    {/* Achievement Amount */}
                    <td className="border border-gray-200 border-l-4 px-2 py-1.5 text-right">
                      {isKeyMetric ? (
                        <div className="flex items-center justify-end gap-1">
                          {row.Achievement_Amt !== null && row.Achievement_Amt !== 0 && (
                            row.Achievement_Amt > 0 ? (
                              <ArrowUp className={`w-3 h-3 ${achievementAmtColor}`} />
                            ) : (
                              <ArrowDown className={`w-3 h-3 ${achievementAmtColor}`} />
                            )
                          )}
                          <span className={`${isKeyMetric ? 'font-semibold' : ''} ${achievementAmtColor}`}>
                            {formatNumber(row.Achievement_Amt, isPercent)}
                          </span>
                        </div>
                      ) : (
                        formatNumber(row.Achievement_Amt, isPercent)
                      )}
                    </td>

                    {/* Achievement % */}
                    <td className="border border-gray-200 px-2 py-1.5 text-right">
                      {isKeyMetric ? (
                        <div className="flex items-center justify-end gap-1">
                          {row.Achievement_Pct !== null &&
                            row.Achievement_Pct !== undefined &&
                            row.Achievement_Pct !== 100 && (
                              row.Achievement_Pct > 100 ? (
                                <ArrowUp className={`w-3 h-3 ${achievementPctColor}`} />
                              ) : (
                                <ArrowDown className={`w-3 h-3 ${achievementPctColor}`} />
                              )
                            )}
                          <span className={`${isKeyMetric ? 'font-semibold' : ''} ${achievementPctColor}`}>
                            {formatNumber(row.Achievement_Pct, true)}
                          </span>
                        </div>
                      ) : (
                        formatNumber(row.Achievement_Pct, true)
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        
        {hasLoadedOnce && !loading && !isLoading && data.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">
              No data available. Please select filters to view data.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PnlSummaryTable;
